import * as React from 'react';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { KeyboardArrowLeft, KeyboardArrowRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';
import { Box, SxProps, Typography, TextField, InputAdornment, Stack, Tooltip } from '@mui/material';
import Search from '@mui/icons-material/Search';
import { Theme } from '@mui/material/styles';
import intl from 'components/i18n/ReactIntlWrapper';
import { memo } from 'react';

function not(a: readonly any[], b: any[]) {
  return a.filter((value) => !b.includes(value));
}

function intersection(a: readonly any[], b: readonly any[]) {
  return a.filter((value) => b.includes(value));
}

const TransferList = ({
  sx,
  getLabel,
  leftItems,
  rightItems,
  transferAll = true,
  onRightChange,
  includeSearchBar = true,
  readOnly = false,
}: {
  sx?: SxProps<Theme>;
  transferAll?: boolean;
  leftItems: any[];
  rightItems: any[];
  includeSearchBar?: boolean;
  getLabel: (item: any) => string;
  onRightChange: (items: any[]) => void;
  readOnly?: boolean;
}) => {
  const [checked, setChecked] = React.useState<readonly any[]>([]);
  const [left, setLeft] = React.useState<readonly any[]>(leftItems);
  const [right, setRight] = React.useState<readonly any[]>(rightItems);
  
  const [leftQuery, setLeftQuery] = React.useState<string>('');
  const [rightQuery, setRightQuery] = React.useState<string>('');
  
  // notify parent when `right` changes
  React.useEffect(() => {
    if (onRightChange) {
      onRightChange(Array.from(right));
    }
  }, [right]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: any) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleCheckedRight = () => {
    if (readOnly) return;
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleAllRight = () => {
    if (readOnly) return;
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleCheckedLeft = () => {
    if (readOnly) return;
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    if (readOnly) return;
    setLeft(left.concat(right));
    setRight([]);
  };

  const renderSearchField = (value: string, onChange: (v: string) => void) => includeSearchBar ? (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'background.paper',
        p: 1,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`
      }}
    >
      <TextField
        size="small"
        fullWidth
        placeholder={intl.get('transferList.searchPlaceholder')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={intl.get('transferList.searchAria')}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  ) : null;

  const customList = (items: readonly any[], query: string, setQuery: (v: string) => void) => {
    const filtered = query
      ? items.filter((it) => getLabel(it).toLowerCase().includes(query.toLowerCase()))
      : items;

    return (<>
      {renderSearchField(query, setQuery)}
      <Paper sx={{ width: '100%', overflow: 'auto' }}>
        {filtered.length ? (
          <List dense component="div" role="list">
            {filtered.map((value: any, index: number) => {
              const labelId = `transfer-list-item-${index}-label`;
              const label = getLabel(value);

              return (
                <Tooltip key={`${label}-${index}`} title={label} arrow enterDelay={1000}>
                  <ListItemButton
                    role="listitem"
                    onClick={handleToggle(value)}
                    selected={checked.indexOf(value) !== -1}
                    sx={{
                      '& .MuiListItemText-primary': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }
                    }}
                  >
                    <ListItemText id={labelId} primary={label} />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        ) : (
          <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Typography variant="button" fontWeight={'bold'}>{intl.get('transferList.noItems')}</Typography>
          </Box>
        )}
      </Paper>
    </>);
  };

  return (
    <Stack
      spacing={2}
      direction={{ xs: 'column', sm: 'row' }}
      width={'100%'}
      sx={{ justifyContent: 'center', alignItems: 'center', height: '100%', ...sx }}
    >
      <Grid size={{ xs: 12, sm: 6 }} sx={{
        height: '100%',
        overflowY: 'auto',
        borderRadius: '.5rem',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        '&:hover': {
          outline: (theme) => `1px solid ${theme.palette.text.primary}`,
        },
        '&:focus-within': {
          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
        },
        color: readOnly ? 'text.disabled' : 'text.primary',
      }} bgcolor={'white'}>
        {customList(left, leftQuery, setLeftQuery)}
      </Grid>
      <Grid size={{ xs: 12, sm: 1 }}>
        <Stack spacing={2} direction={{ xs: 'row-reverse', sm: 'column' }} sx={{ alignItems: 'center', justifyContent: 'center' }}>
          {transferAll ? (<Button
            variant="outlined"
            size="small"
            onClick={handleAllRight}
            disabled={left.length === 0 || readOnly}
            aria-label="move all right"
          >
            <KeyboardDoubleArrowRight sx={{ transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
          </Button>) : null}
          <Button
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0 || readOnly}
            aria-label="move selected right"
          >
            <KeyboardArrowRight sx={{ transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0 || readOnly}
            aria-label="move selected left"
          >
            <KeyboardArrowLeft sx={{ transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
          </Button>
          {transferAll ? (<Button
            variant="outlined"
            size="small"
            onClick={handleAllLeft}
            disabled={right.length === 0 || readOnly}
            aria-label="move all left"
          >
            <KeyboardDoubleArrowLeft sx={{ transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
          </Button>) : null}
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{
        height: '100%',
        overflowY: 'auto',
        borderRadius: '.5rem',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        '&:hover': {
          outline: (theme) => `1px solid ${theme.palette.text.primary}`,
        },
        '&:focus-within': {
          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
        },
        color: readOnly ? 'text.disabled' : 'text.primary',
      }} bgcolor={'white'}>
        {customList(right, rightQuery, setRightQuery)}
      </Grid>
    </Stack>
  );
}

export default memo(TransferList);
